export interface BrandConfig {
  name: string;
  logo: string;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    gradient: {
      start: string;
      end: string;
      angle: number;
    };
  };
  theme: {
    headerGradient: string;
    buttonGradient: string;
    cardGradient?: string;
    backgroundColor?: string;
  };
}

// Default DV branding
export const dvBrand: BrandConfig = {
  name: 'DV System',
  logo: '/dv-logo.png', // You can add your current logo here
  colors: {
    primary: '#667eea',
    secondary: '#764ba2',
    accent: '#FE6B8B',
    gradient: {
      start: '#667eea',
      end: '#764ba2',
      angle: 135,
    },
  },
  theme: {
    headerGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    buttonGradient: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
    backgroundColor: '#F5F7FA',
  },
};

// GolfOS branding
export const golfosBrand: BrandConfig = {
  name: 'GolfOS',
  logo: '/golfos-logo.png',
  colors: {
    primary: '#2D6A2F',
    secondary: '#1A3F1B',
    accent: '#8DC63F',
    gradient: {
      start: '#2D6A2F',
      end: '#1A3F1B',
      angle: 135,
    },
  },
  theme: {
    headerGradient: 'linear-gradient(135deg, #2D6A2F 0%, #1A3F1B 100%)',
    buttonGradient: 'linear-gradient(45deg, #8DC63F 30%, #5A9E2F 90%)',
    cardGradient: 'linear-gradient(135deg, #2D6A2F 0%, #1A3F1B 100%)',
    backgroundColor: '#F4F8F4',
  },
};

// Distill branding
export const distillBrand: BrandConfig = {
  name: 'Distill',
  logo: '/distill-logo.png',
  colors: {
    primary: '#FF933E', // Tangerine
    secondary: '#E01C24', // Cherry
    accent: '#FF933E',
    gradient: {
      start: '#FF933E',
      end: '#E01C24',
      angle: -55, // -55 degrees as shown in brand guide
    },
  },
  theme: {
    headerGradient: 'linear-gradient(-55deg, #FF933E 30%, #E01C24 100%)',
    buttonGradient: 'linear-gradient(-55deg, #FF933E 30%, #E01C24 90%)',
    cardGradient: 'linear-gradient(-55deg, #FF933E 0%, #E01C24 100%)',
    backgroundColor: '#FFF8F4',
  },
};

// Available brands
export const availableBrands = {
  dv: dvBrand,
  distill: distillBrand,
  golfos: golfosBrand,
} as const;

export type BrandKey = keyof typeof availableBrands;

// Current brand selection driven by VITE_BRAND_KEY env variable
const currentBrandKey = (import.meta.env.VITE_BRAND_KEY as BrandKey) || 'distill';

export const currentBrand = availableBrands[currentBrandKey] || distillBrand;

/**
 * GolfOS-only UI, tenant lock, and feature defaults apply **only** when both are set
 * (as in `.env.golfos` / `npm run build:golfos`). Main Distill HQ uses `build:prod` and
 * must not set these — a lone `VITE_APP_VARIANT=golfos` without `VITE_BRAND_KEY=golfos`
 * will **not** activate GolfOS behavior.
 */
export const isGolfOSVariant =
  import.meta.env.VITE_APP_VARIANT === 'golfos' &&
  import.meta.env.VITE_BRAND_KEY === 'golfos';

/** GolfOS builds use a single fixed tenant (no tenant switcher in UI). */
export const GOLFOS_TENANT_CODE = 'magna';

// Utility function to get brand-specific styles
export const getBrandStyles = (brand: BrandConfig = currentBrand) => ({
  colors: brand.colors,
  theme: brand.theme,
  css: {
    '--brand-primary': brand.colors.primary,
    '--brand-secondary': brand.colors.secondary,
    '--brand-accent': brand.colors.accent,
    '--brand-gradient': brand.theme.headerGradient,
    '--brand-button-gradient': brand.theme.buttonGradient,
  },
});

// React hook for using brand context
export const useBrand = () => {
  return {
    brand: currentBrand,
    styles: getBrandStyles(currentBrand),
  };
};
